import {
  api,
  type AuthUser,
  type ComponentItem,
  type ComponentVersion,
  type Organization,
} from '../api/client.js';
import { openAddComponentModal } from '../components/add-component-modal.js';
import { openDeleteComponentModal } from '../components/delete-component-modal.js';
import { openDeleteVersionModal } from '../components/delete-version-modal.js';
import { openInviteUserModal } from '../components/invite-user-modal.js';
import { openPromoteVersionModal } from '../components/promote-version-modal.js';
import { openRotateTokenModal } from '../components/rotate-token-modal.js';
import { copyTextWithFeedback } from '../util/copy-feedback.js';

type Tab = 'mine' | 'team';

interface ExpansionState {
  loading: boolean;
  versions: ComponentVersion[] | null;
  error: string | null;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

function renderExpansionBody(
  state: ExpansionState | undefined,
  versionsMutable: boolean,
): string {
  if (!state || state.loading) {
    return `<div class="cc-card__body-loading">
        <sl-spinner style="font-size:1rem"></sl-spinner>
        <span>Loading versions…</span>
      </div>`;
  }
  if (state.error) {
    return `<div class="cc-card__body-error">${escapeHtml(state.error)}</div>`;
  }
  const versions = state.versions ?? [];
  if (versions.length === 0) {
    return `<div class="cc-card__body-empty">No versions published yet.</div>`;
  }
  return versions
    .map(
      (v) => `
        <section class="cc-version">
          <header class="cc-version__header">
            <span class="cc-version__tag">v${v.version}</span>
            ${
              v.is_current
                ? '<sl-badge variant="success" pill>current</sl-badge>'
                : ''
            }
            <span class="cc-version__date">${escapeHtml(formatDate(v.created_at))}</span>
            <span class="cc-version__author">${escapeHtml(v.created_by_email)}</span>
            <span class="cc-version__count">${v.files.length} file${
              v.files.length === 1 ? '' : 's'
            }</span>
            ${
              versionsMutable && !v.is_current
                ? `<sl-icon-button
                    class="cc-version__promote"
                    name="arrow-up-circle"
                    label="Make v${v.version} current"
                    data-version="${v.version}"
                    style="color: var(--sl-color-primary-600)"></sl-icon-button>`
                : ''
            }
            ${
              versionsMutable
                ? `<sl-icon-button
                    class="cc-version__delete"
                    name="trash"
                    label="Delete version v${v.version}"
                    data-version="${v.version}"></sl-icon-button>`
                : ''
            }
          </header>
          ${
            v.message
              ? `<div class="cc-version__message">${escapeHtml(v.message)}</div>`
              : ''
          }
          ${
            v.files.length === 0
              ? `<div class="cc-version__empty">No files recorded for this version.</div>`
              : `<ul class="cc-file-list">
                   ${v.files
                     .map(
                       (f) => `<li class="cc-file">
                         <i class="fa fa-file-o" aria-hidden="true"></i>
                         <a class="cc-file__link"
                            href="${escapeAttr(f.url)}"
                            target="_blank"
                            rel="noopener">${escapeHtml(f.path)}</a>
                       </li>`,
                     )
                     .join('')}
                 </ul>`
          }
        </section>`,
    )
    .join('');
}

function renderCard(
  it: ComponentItem,
  expanded: boolean,
  state: ExpansionState | undefined,
  deletable: boolean,
): string {
  const chevron = expanded ? 'chevron-up' : 'chevron-down';
  const ariaLabel = expanded ? 'Collapse' : 'Expand';
  // Mutating controls (delete, token rotation) only render for cards the
  // viewer owns ("My CC" tab). Team / by-org views never offer them because
  // the backend 404s such requests against non-owned components anyway.
  const rotateBtn = deletable
    ? `<sl-icon-button
        class="cc-card__rotate-token"
        name="arrow-repeat"
        label="Rotate publish token"
        style="margin-left:auto; color: var(--sl-color-warning-600)"></sl-icon-button>`
    : '';
  const deleteBtn = deletable
    ? `<sl-icon-button
        class="cc-card__delete"
        name="trash"
        label="Delete component"
        style="color: var(--sl-color-danger-600)"></sl-icon-button>`
    : '';
  // Show both pointers once they diverge: `latest` is the newest uploaded
  // version, `current` is what pasted snippets actually serve.
  const latest = it.latest_version ?? 0;
  const current = it.current_version;
  const versionSummary =
    current != null && current !== latest
      ? `latest v${latest} &middot; current v${current}`
      : current == null && latest > 0
        ? `latest v${latest} &middot; nothing live`
        : `v${latest}`;
  return `
    <div class="cc-card" data-id="${escapeAttr(it.id)}" data-name="${escapeAttr(it.name)}">
      <div class="cc-card__title">
        <i class="fa fa-puzzle-piece" aria-hidden="true"></i>
        <span>${escapeHtml(it.name)}</span>
        ${it.is_active ? '' : '<sl-badge variant="warning">deactivated</sl-badge>'}
        ${rotateBtn}
        ${deleteBtn}
        <sl-icon-button
          class="cc-card__toggle"
          name="${chevron}"
          label="${ariaLabel}"
          aria-expanded="${expanded ? 'true' : 'false'}"
          style="${deletable ? '' : 'margin-left:auto'}"></sl-icon-button>
      </div>
      <div class="cc-card__id">
        <span class="cc-card__id-label">ID</span>
        <code class="cc-card__id-value">${escapeHtml(it.id)}</code>
        <sl-icon-button
          class="cc-card__copy-id copy-feedback"
          name="clipboard"
          label="Copy component ID"></sl-icon-button>
      </div>
      <div class="cc-card__meta">
        ${versionSummary} &middot; ${escapeHtml(it.owner_email)} &middot; updated ${escapeHtml(
          formatDate(it.updated_at),
        )}
      </div>
      ${
        it.description
          ? `<div class="cc-card__description">${escapeHtml(it.description)}</div>`
          : ''
      }
      ${
        expanded
          ? `<div class="cc-card__body">${renderExpansionBody(state, deletable)}</div>`
          : ''
      }
    </div>`;
}

interface ListController {
  rerender(): void;
  setItems(items: ComponentItem[]): void;
  destroy(): void;
}

interface ListControllerOptions {
  /** When true, each card gets a delete icon-button. Used only for
   *  the "My CC" tab; team/by-org views are read-only. */
  deletable?: boolean;
  /** Invoked after the user successfully deletes a component, so the
   *  caller can refresh siblings (e.g. force the team tab to reload). */
  onDeleted?: (componentId: string) => void;
  /** Invoked after a single version is deleted and latest_version may change. */
  onVersionDeleted?: (componentId: string) => void;
  /** Invoked after a version is promoted and current_version changes. */
  onVersionPromoted?: (componentId: string) => void;
}

function attachListController(
  target: HTMLElement,
  initialItems: ComponentItem[],
  options: ListControllerOptions = {},
): ListController {
  let items = initialItems;
  const expanded = new Set<string>();
  const states = new Map<string, ExpansionState>();
  const deletable = options.deletable === true;

  function rerender(): void {
    if (items.length === 0) {
      target.innerHTML = `
        <div class="empty-state">
          <sl-icon name="inbox" style="font-size:2rem;display:block;margin-bottom:0.5rem"></sl-icon>
          <div>No components yet.</div>
        </div>`;
      return;
    }
    target.innerHTML = items
      .map((it) => renderCard(it, expanded.has(it.id), states.get(it.id), deletable))
      .join('');
  }

  async function loadVersions(componentId: string): Promise<void> {
    states.set(componentId, { loading: true, versions: null, error: null });
    rerender();
    try {
      const { versions } = await api.listComponentVersions(componentId);
      states.set(componentId, { loading: false, versions, error: null });
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to load versions';
      states.set(componentId, { loading: false, versions: null, error: message });
    }
    rerender();
  }

  async function handleDelete(card: HTMLElement, id: string): Promise<void> {
    const name = card.dataset.name ?? '';
    const ok = await openDeleteComponentModal({ id, name });
    if (!ok) return;
    items = items.filter((it) => it.id !== id);
    expanded.delete(id);
    states.delete(id);
    rerender();
    options.onDeleted?.(id);
  }

  async function handleDeleteVersion(
    card: HTMLElement,
    componentId: string,
    version: number,
  ): Promise<void> {
    const componentName = card.dataset.name ?? '';
    const isCurrent =
      states.get(componentId)?.versions?.find((v) => v.version === version)
        ?.is_current === true;
    const ok = await openDeleteVersionModal({ componentName, version, isCurrent });
    if (!ok) return;

    const previousState = states.get(componentId);
    states.set(componentId, { loading: true, versions: null, error: null });
    rerender();
    try {
      const result = await api.deleteComponentVersion(componentId, version);
      items = items.map((it) =>
        it.id === componentId
          ? {
              ...it,
              latest_version: result.latestVersion,
              current_version: result.currentVersion,
              updated_at: result.updated_at,
            }
          : it,
      );
      options.onVersionDeleted?.(componentId);
      await loadVersions(componentId);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to delete version';
      states.set(componentId, {
        loading: false,
        versions: previousState?.versions ?? null,
        error: message,
      });
      rerender();
    }
  }

  async function handlePromoteVersion(
    card: HTMLElement,
    componentId: string,
    version: number,
  ): Promise<void> {
    const componentName = card.dataset.name ?? '';
    const currentVersion =
      items.find((it) => it.id === componentId)?.current_version ?? null;
    const ok = await openPromoteVersionModal({ componentName, version, currentVersion });
    if (!ok) return;

    const previousState = states.get(componentId);
    states.set(componentId, { loading: true, versions: null, error: null });
    rerender();
    try {
      const result = await api.promoteComponentVersion(componentId, version);
      items = items.map((it) =>
        it.id === componentId
          ? {
              ...it,
              current_version: result.currentVersion,
              updated_at: result.updated_at,
            }
          : it,
      );
      options.onVersionPromoted?.(componentId);
      await loadVersions(componentId);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to promote version';
      states.set(componentId, {
        loading: false,
        versions: previousState?.versions ?? null,
        error: message,
      });
      rerender();
    }
  }

  async function handleRotateToken(card: HTMLElement, componentId: string): Promise<void> {
    const componentName = card.dataset.name ?? '';
    await openRotateTokenModal({ componentId, componentName });
    // Nothing in the list changes visually — the token is not displayed —
    // but updated_at moved; a full reload isn't worth the flicker.
  }

  function onClick(ev: Event): void {
    const target = ev.target as HTMLElement | null;
    if (!target) return;

    const copyIdEl = target.closest('.cc-card__copy-id') as HTMLElement | null;
    if (copyIdEl) {
      const card = copyIdEl.closest('.cc-card') as HTMLElement | null;
      const id = card?.dataset.id;
      if (!id) return;
      ev.stopPropagation();
      void copyTextWithFeedback(id, copyIdEl, { copiedLabel: 'Copied' });
      return;
    }

    const deleteEl = target.closest('.cc-card__delete') as HTMLElement | null;
    if (deleteEl) {
      const card = deleteEl.closest('.cc-card') as HTMLElement | null;
      const id = card?.dataset.id;
      if (!card || !id) return;
      ev.stopPropagation();
      void handleDelete(card, id);
      return;
    }

    const rotateEl = target.closest('.cc-card__rotate-token') as HTMLElement | null;
    if (rotateEl) {
      const card = rotateEl.closest('.cc-card') as HTMLElement | null;
      const id = card?.dataset.id;
      if (!card || !id) return;
      ev.stopPropagation();
      void handleRotateToken(card, id);
      return;
    }

    const versionDeleteEl = target.closest('.cc-version__delete') as HTMLElement | null;
    if (versionDeleteEl) {
      const card = versionDeleteEl.closest('.cc-card') as HTMLElement | null;
      const id = card?.dataset.id;
      const version = Number(versionDeleteEl.dataset.version);
      if (!card || !id || !Number.isInteger(version)) return;
      ev.stopPropagation();
      void handleDeleteVersion(card, id, version);
      return;
    }

    const promoteEl = target.closest('.cc-version__promote') as HTMLElement | null;
    if (promoteEl) {
      const card = promoteEl.closest('.cc-card') as HTMLElement | null;
      const id = card?.dataset.id;
      const version = Number(promoteEl.dataset.version);
      if (!card || !id || !Number.isInteger(version)) return;
      ev.stopPropagation();
      void handlePromoteVersion(card, id, version);
      return;
    }

    const toggle = target.closest('.cc-card__toggle');
    if (!toggle) return;
    const card = toggle.closest('.cc-card') as HTMLElement | null;
    const id = card?.dataset.id;
    if (!id) return;
    ev.stopPropagation();

    if (expanded.has(id)) {
      expanded.delete(id);
      rerender();
      return;
    }
    expanded.add(id);
    const existing = states.get(id);
    if (!existing || (!existing.versions && !existing.error && !existing.loading)) {
      void loadVersions(id);
    } else {
      rerender();
    }
  }

  target.addEventListener('click', onClick);
  rerender();

  return {
    rerender,
    setItems(next: ComponentItem[]) {
      items = next;
      // Drop expansion state for items no longer in the list.
      const presentIds = new Set(next.map((n) => n.id));
      for (const id of [...expanded]) if (!presentIds.has(id)) expanded.delete(id);
      for (const id of [...states.keys()]) if (!presentIds.has(id)) states.delete(id);
      rerender();
    },
    destroy() {
      target.removeEventListener('click', onClick);
    },
  };
}

export function renderComponentsListPage(
  root: HTMLElement,
  user: AuthUser,
  onLogout: () => void,
): void {
  root.innerHTML = `
    <div class="app-shell">
      <header class="app-header">
        <div class="app-header__brand">
          <svg class="app-header__logo" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" width="32" height="32" aria-hidden="true">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M40 7.69992V32.3001C40 36.5526 36.5526 40 32.3001 40H7.69992C3.44736 40 0 36.5526 0 32.3001V7.69992C0 3.44737 3.44736 0 7.69992 0L32.3001 0C36.5526 0 40 3.44737 40 7.69992Z" fill="url(#sa-logo-gradient)"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M26.7298 22.1439C24.5012 18.5238 15.6254 16.8804 15.9488 12.7279C16.2018 9.47748 20.7447 9.79208 20.7447 9.79208C20.7447 9.79208 13.6014 9.03088 12.607 14.1546C11.5862 19.4093 22.2011 21.3948 23.6839 24.4945C26.0555 29.4555 18.47 29.9318 18.47 29.9318C26.6462 30.3102 28.6163 25.2062 26.7287 22.1439H26.7298Z" fill="white"></path>
            <path opacity="0.5" fill-rule="evenodd" clip-rule="evenodd" d="M15.5242 28.6183L15.2349 28.5062C15.1403 28.4622 15.049 28.4127 14.9544 28.3654L14.8136 28.2917C14.7685 28.2653 14.7256 28.2334 14.6805 28.2048C14.5925 28.1454 14.5013 28.0893 14.4144 28.0233C14.0734 27.7615 13.7632 27.4359 13.5267 27.0608C13.2869 26.6868 13.1241 26.2644 13.0427 25.8376C12.9602 25.4108 12.9558 24.9807 13.0185 24.5726C13.0812 24.1623 13.2044 23.7718 13.376 23.411C13.552 23.0535 13.7687 22.7147 14.0613 22.4386C14.0206 23.2229 14.0107 23.9346 14.0976 24.5968C14.1823 25.2568 14.3726 25.8398 14.6926 26.3381C14.8554 26.5856 15.0413 26.8243 15.2657 27.0432C15.4945 27.2588 15.7409 27.4744 16.0214 27.668C16.1556 27.7747 16.3096 27.8583 16.4526 27.9617C16.6033 28.0563 16.7573 28.1487 16.9124 28.2477C17.0708 28.339 17.2314 28.4347 17.3942 28.5348C17.5592 28.6261 17.7264 28.7283 17.8936 28.8416C17.6428 28.8845 17.3876 28.9054 17.1346 28.9054C16.5934 28.9054 16.0522 28.8086 15.5242 28.6195V28.6183ZM25.3735 15.0038C25.2899 14.3438 25.0996 13.7608 24.7795 13.2625C24.6167 13.015 24.4309 12.7763 24.2065 12.5574C23.9755 12.3418 23.7313 12.1273 23.4486 11.9326C23.3166 11.8259 23.1626 11.7434 23.0196 11.6389C22.8689 11.5443 22.7149 11.4519 22.5587 11.3529C22.3992 11.2627 22.2397 11.1659 22.0769 11.0658C21.9108 10.9745 21.7447 10.8722 21.5775 10.7589C21.8294 10.716 22.0835 10.6951 22.3365 10.6951C22.8777 10.6951 23.42 10.7919 23.9469 10.9811L24.2362 11.0944C24.3297 11.1384 24.4232 11.1879 24.5156 11.2363L24.6563 11.3089C24.7036 11.3353 24.7454 11.3672 24.7916 11.3958C24.8796 11.4563 24.9698 11.5113 25.0556 11.5773C25.3988 11.8391 25.7068 12.1647 25.9455 12.5398C26.1853 12.9138 26.347 13.3362 26.4295 13.763C26.5109 14.1898 26.5142 14.621 26.4515 15.0291C26.3888 15.4383 26.2678 15.8288 26.0951 16.1896C25.9191 16.5471 25.7035 16.8859 25.4109 17.162C25.4505 16.3788 25.4604 15.666 25.3735 15.0038Z" fill="white"></path>
            <defs>
              <linearGradient id="sa-logo-gradient" x1="39.5704" y1="0.4296" x2="1.14244e-06" y2="40" gradientUnits="userSpaceOnUse">
                <stop stop-color="#FF8945"></stop>
                <stop offset="1" stop-color="#FF22A7"></stop>
              </linearGradient>
            </defs>
          </svg>
          <span>Custom Components</span>
        </div>
        <div style="display:flex;align-items:center;gap:0.75rem">
          <span style="color:var(--sl-color-neutral-600);font-size:0.875rem">
            ${escapeHtml(user.email ?? user.id)}
          </span>
          ${
            user.role === 'superadmin' || user.role === 'org_admin'
              ? `<sl-button id="invite-btn" size="small" variant="default">
                   <sl-icon slot="prefix" name="person-plus"></sl-icon>
                   Invite users
                 </sl-button>`
              : ''
          }
          <sl-button id="logout-btn" size="small" variant="default">Sign out</sl-button>
        </div>
      </header>

      <main class="app-main">
        <div class="cc-list-toolbar">
          <h2 style="margin:0">Custom components</h2>
          <sl-button id="add-btn" variant="primary">
            <sl-icon slot="prefix" name="plus-lg"></sl-icon>
            Add new
          </sl-button>
        </div>

        <sl-tab-group id="tabs">
          <sl-tab slot="nav" panel="mine">My custom components</sl-tab>
          <sl-tab slot="nav" panel="team">${
            user.role === 'superadmin'
              ? 'Other organizations'
              : 'Team custom components'
          }</sl-tab>

          <sl-tab-panel name="mine">
            <div class="cc-list" id="list-mine">Loading…</div>
          </sl-tab-panel>
          <sl-tab-panel name="team">
            ${
              user.role === 'superadmin'
                ? `<div class="cc-org-picker">
                     <label for="org-picker" style="font-size:0.875rem; font-weight:500">
                       Pick an organization to view its components
                     </label>
                     <sl-select
                       id="org-picker"
                       placeholder="Select an organization\u2026"
                       hoist
                       clearable
                       style="margin-top:0.375rem"></sl-select>
                   </div>
                   <div class="cc-list" id="list-team" style="margin-top:0.75rem"></div>`
                : `<div class="cc-list" id="list-team">Loading\u2026</div>`
            }
          </sl-tab-panel>
        </sl-tab-group>
      </main>
    </div>
  `;

  const listMine = root.querySelector<HTMLElement>('#list-mine')!;
  const listTeam = root.querySelector<HTMLElement>('#list-team')!;

  let mineCtl: ListController | null = null;
  let teamCtl: ListController | null = null;
  // For regular users: the team-tab list is loaded once and cached.
  // For superadmin: the team-tab is per-org; this flag is unused.
  let loadedTeam = false;

  async function loadMine() {
    listMine.textContent = 'Loading…';
    try {
      const { items } = await api.listMine();
      if (mineCtl) {
        mineCtl.setItems(items);
      } else {
        mineCtl = attachListController(listMine, items, {
          deletable: true,
          onDeleted: () => {
            // The team tab caches its load on first show; if the user
            // ever flips to it again it should refetch (a deleted CC
            // would otherwise still appear there for teammates of the
            // owner during the same session).
            loadedTeam = false;
            teamCtl?.destroy();
            teamCtl = null;
            if (user.role === 'superadmin' && lastPickedOrgId) {
              void loadByOrg(lastPickedOrgId);
            }
          },
          onVersionDeleted: () => {
            loadedTeam = false;
            teamCtl?.destroy();
            teamCtl = null;
            if (user.role === 'superadmin' && lastPickedOrgId) {
              void loadByOrg(lastPickedOrgId);
            }
          },
          onVersionPromoted: () => {
            loadedTeam = false;
            teamCtl?.destroy();
            teamCtl = null;
            if (user.role === 'superadmin' && lastPickedOrgId) {
              void loadByOrg(lastPickedOrgId);
            }
          },
        });
      }
    } catch {
      mineCtl?.destroy();
      mineCtl = null;
      listMine.innerHTML = '<div class="empty-state">Failed to load components.</div>';
    }
  }

  async function loadTeam() {
    if (loadedTeam) return;
    listTeam.textContent = 'Loading…';
    try {
      const { items } = await api.listTeam();
      if (teamCtl) {
        teamCtl.setItems(items);
      } else {
        teamCtl = attachListController(listTeam, items);
      }
      loadedTeam = true;
    } catch {
      teamCtl?.destroy();
      teamCtl = null;
      listTeam.innerHTML = '<div class="empty-state">Failed to load components.</div>';
    }
  }

  // Superadmin-only: the org picker that drives the second tab. Lazy-
  // loads the picker options on first tab focus, then loads the
  // selected org's components on each change. Cleared selection
  // empties the list.
  let orgsLoaded = false;
  let lastPickedOrgId: string | null = null;

  async function ensureOrgsLoaded(): Promise<void> {
    if (orgsLoaded) return;
    const picker = root.querySelector<HTMLElement>('#org-picker');
    if (!picker) return;
    try {
      const { items } = await api.listOrganizations();
      // Exclude the superadmin's own org — that view is reachable
      // via "My custom components" already, and the backend rejects
      // it with 400 to keep the contract clean.
      const others = items.filter((o: Organization) => o.id !== user.organizationId);
      if (others.length === 0) {
        picker.outerHTML =
          '<div class="empty-state" style="padding:1rem">No other organizations exist yet.</div>';
        orgsLoaded = true;
        return;
      }
      picker.innerHTML = others
        .map(
          (o: Organization) =>
            `<sl-option value="${escapeAttr(o.id)}">${escapeHtml(o.name)}</sl-option>`,
        )
        .join('');
      orgsLoaded = true;
    } catch (err) {
      console.error('[components-list] failed to load organizations', err);
      listTeam.innerHTML =
        '<div class="empty-state">Failed to load organizations.</div>';
    }
  }

  async function loadByOrg(organizationId: string): Promise<void> {
    lastPickedOrgId = organizationId;
    listTeam.textContent = 'Loading…';
    try {
      const { items } = await api.listComponentsByOrg(organizationId);
      if (teamCtl) {
        teamCtl.setItems(items);
      } else {
        teamCtl = attachListController(listTeam, items);
      }
    } catch {
      teamCtl?.destroy();
      teamCtl = null;
      listTeam.innerHTML = '<div class="empty-state">Failed to load components.</div>';
    }
  }

  function clearOrgList(): void {
    lastPickedOrgId = null;
    teamCtl?.destroy();
    teamCtl = null;
    listTeam.innerHTML = '';
  }

  root.querySelector<HTMLElement>('#tabs')!.addEventListener('sl-tab-show', (ev) => {
    const detail = (ev as CustomEvent<{ name: Tab }>).detail;
    if (detail.name !== 'team') return;
    if (user.role === 'superadmin') {
      void ensureOrgsLoaded();
    } else {
      void loadTeam();
    }
  });

  // Wire the superadmin's org picker. Listens at the document level
  // because the <sl-select> may be re-rendered if no other orgs
  // exist (we replace it with an empty-state message).
  root.addEventListener('sl-change', (ev) => {
    const target = ev.target as (HTMLElement & { value?: string }) | null;
    if (!target || target.id !== 'org-picker') return;
    const value = target.value ?? '';
    if (value) {
      void loadByOrg(value);
    } else {
      clearOrgList();
    }
  });

  root.querySelector<HTMLElement>('#add-btn')!.addEventListener('click', async () => {
    const result = await openAddComponentModal();
    if (result) {
      loadedTeam = false;
      teamCtl?.destroy();
      teamCtl = null;
      // For superadmin, refresh the currently-displayed org list too
      // (their "Add new" doesn't affect another org, but be safe).
      if (user.role === 'superadmin' && lastPickedOrgId) {
        await loadByOrg(lastPickedOrgId);
      }
      await loadMine();
    }
  });

  root.querySelector<HTMLElement>('#logout-btn')!.addEventListener('click', async () => {
    await api.logout().catch(() => undefined);
    onLogout();
  });

  const inviteBtn = root.querySelector<HTMLElement>('#invite-btn');
  if (inviteBtn) {
    inviteBtn.addEventListener('click', () => {
      void openInviteUserModal(user);
    });
  }

  void loadMine();
}
